<template>
  <div :style="style" class="relative overflow-hidden">
    <div v-if="aspectRatio !== false" class="w-full" style="padding-bottom: 56.25%"></div>
    <OImage
      v-if="isVideoArticle"
      loading="lazy"
      src="/images/video-play-button.svg"
      class="absolute z-1 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-2/5 w-2/5 pointer-events-none"
    />
    <div :class="{ 'absolute inset-0': aspectRatio !== false, 'w-full h-full': aspectRatio === false }">
      <figure v-if="previewUrl && showPreview" class="bg-black w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          :alt="article.title"
          class="video-preview object-center"
          :class="{ 'w-full h-full': aspectRatio === false || aspectRatio === 'cover' }"
        >
          <source type="video/webm" :src="previewUrl" />
        </video>
      </figure>
      <OImage
        v-else
        ref="imgEl"
        :loading="lazy ? 'lazy' : 'eager'"
        :src="imgUrl"
        :alt="article.title"
        class="object-cover"
        :class="{ 'w-full h-full': aspectRatio === false || aspectRatio === 'cover' }"
      />
    </div>
    <slot />
  </div>
</template>
<script>
import { defineComponent, inject, computed, ref, onMounted, onBeforeUnmount } from '@nuxtjs/composition-api';
import { hasArticleCategory } from '@/modules/article/utils/articleUtils';
import { getImageUrl } from '~/modules/common/utils/imageUtils';
import { eventBus } from '~/modules/common/utils/eventBus';
import { OImage } from '~/modules/common/components/ui';

export default defineComponent({
  components: { OImage },
  props: {
    width: {
      type: [String, Number],
      default: '100%',
    },
    height: {
      type: [String, Number],
      default: 'auto',
    },
    aspectRatio: {
      type: [String, Number],
      default: 'cover',
    },
    showVideoIcon: {
      type: Boolean,
      default: true,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const showPreview = ref(false);
    const article = inject('article', null);

    const isShowPreview = ({ id, status }) => {
      if (article._id !== id) return;
      showPreview.value = status;
    };

    onMounted(() => {
      eventBus.on('playVideo', isShowPreview);
    });

    onBeforeUnmount(() => {
      eventBus.off('playVideo', isShowPreview);
    });

    const width = computed(() => (props.width === '100%' ? null : Number(props.width)));
    const height = computed(() => (props.height === 'auto' ? null : Number(props.height)));
    const imgUrl = computed(() => getImageUrl(article?.image, width.value ? { width: width.value * 2 } : { standard: true }) || '');
    const previewUrl = computed(() => article?.preview);
    const isVideoArticle = props.showVideoIcon && (hasArticleCategory(article, 'Video') || previewUrl.value);
    const style = computed(() => ({
      width: width.value ? width.value + 'px' : '100%',
      height: height.value ? height.value + 'px' : 'auto',
    }));

    return { article, isVideoArticle, imgUrl, style, previewUrl, showPreview, isShowPreview };
  },
});
</script>

<style lang="scss">
.video-preview {
  display: block;
  position: relative;
  animation: 0.7s ease-in 0s both running opacity;
}

@keyframes opacity {
  0% {
    filter: opacity(10%);
  }
  50% {
    filter: opacity(60%);
  }
  100% {
    filter: opacity(100%);
  }
}
</style>
